import { Back, Power0, TweenMax } from 'gsap';
import { Container, Sprite } from 'pixi.js';
import { GameAudio } from '../../Config/Audio';
import { getGameSize } from '../../Config/Levels';
import Core from '../../Core/Core';
import { gameConfig } from '../../index';
import { Popup } from '../../Managers/Popups';
import SoundManager from '../../Sound/SoundManager';
import { waitForMs } from '../../Utils/AsyncUtils';
import { getRandomFloat } from '../../Utils/Utils';
import HudView from '../../View/HudView';
import { Confetti } from '../Animations/Confetti';
import { ITweenable } from '../PopUps/ITweenable';
import { PopupHeader } from '../PopUps/PopupHeader';
import { SocialRewardBox } from '../PopUps/Social/SocialRewardBox';
import { SocialTabBtn } from '../PopUps/Social/SocialTabBtn';
import { RewardBox } from '../PopUps/Win/RewardBox';
import { TextButton } from './Buttons/TextButton';
import { UIButton } from './Buttons/UIButton';
import { PopupPage } from './PopupPage';
import { ScreenBlur } from './ScreenBlur';
import SagaTracking from '../../Managers/Tracking/SagaTracking';

/**
 * base class for all map bottom ui popups
 *
 */

// const logger: ILogger = getLogger('[SOCIALHUBPOPUP]:');

export abstract class MapHubPopup extends PIXI.Container implements ITweenable {
    public allPagesContainer: Container;
    public tabsContainer: Container;
    public allPages: PopupPage[] = [];
    public btns: UIButton[] = []; // all other buttons
    public tabs: SocialTabBtn[] = []; // all the tabs buttons
    public bgTab: PIXI.mesh.NineSlicePlane;
    public playerData: any[];
    public hudView: HudView;
    public curActiveTab: number = 0;
    protected bg: PIXI.Container;
    protected header: PopupHeader;
    private clouds: PIXI.Sprite[] = [];
    private confetti: Confetti;

    protected constructor(
        headerTextKey: string,
        hudView: HudView,
        bgWidth: number,
        bgHeight: number,
        bgYPos: number,
        popupType: Popup
    ) {
        super();
        const gameSize = getGameSize();

        this.bg = new Sprite(PIXI.utils.TextureCache['loading/win-bg']);
        this.bg.interactive = true;
        this.bg.width = gameSize.gameWidth;
        this.bg.height = gameSize.gameHeight;

        this.header = new PopupHeader(headerTextKey, popupType);

        this.hudView = hudView;
        this.alpha = 0;

        this.addClouds();

        const texture = PIXI.utils.TextureCache['commonui/leaderboard-bg'];
        const bgTab = new PIXI.mesh.NineSlicePlane(texture, 50, 50, 50, 50);

        bgTab.width = bgWidth;
        bgTab.height = bgHeight;
        bgTab.x = gameSize.gameWidth / 2;
        bgTab.y = bgYPos;
        bgTab.pivot.x = bgTab.width / 2;
        bgTab.pivot.y = bgTab.height / 2;

        this.tabsContainer = new Container();
        this.tabsContainer.x = bgTab.x - bgTab.width / 2;
        this.tabsContainer.y = bgTab.y;

        bgTab.scale.set(0);

        this.bgTab = bgTab;

        this.allPagesContainer = new Container();
        const maskingContainer = new Container();
        const mask = new PIXI.Graphics();
        mask.beginFill(0x000000);
        mask.drawRoundedRect(bgTab.x, 0, bgTab.width, gameSize.gameHeight, 10);
        mask.pivot.x = bgTab.width / 2;
        this.addChild(mask);
        maskingContainer.addChild(this.allPagesContainer);
        maskingContainer.mask = mask;
        this.addChild(
            this.bg,
            ...this.clouds,
            this.header,
            this.tabsContainer,
            this.bgTab,
            maskingContainer
        );
    }

    public addTab(tabTextKey: string) {
        const tab = new SocialTabBtn(Core.local.getText(tabTextKey));
        tab.tabKey = tabTextKey;
        if (this.tabs.length === 0) {
            tab.setActiveTab(true);
        }
        tab.x = tab.bgActive.width / 2 + this.tabs.length * tab.bgActive.width;
        tab.y = -this.bgTab.height / 2;
        this.tabs.push(tab);
        this.tabsContainer.addChild(tab);
        tab.on('pointerdown', this.handleTab.bind(this));
        return tab;
    }

    public addPage(page: PopupPage) {
        page.pivot.x = this.bgTab.width / 2;
        page.pivot.y = this.bgTab.height / 2;
        page.x = this.bgTab.x + this.allPages.length * this.bgTab.width;
        page.y = this.bgTab.y;
        this.allPagesContainer.addChild(page);
        this.allPages.push(page);
    }

    public addButton(button: UIButton) {
        this.btns.push(button);
        this.addChild(button);
        button.on('trigger', this.handleBtn.bind(this));
    }

    /**
     * fancy tween in of all the popup objects
     */
    public async tweenIn() {
        this.alpha = 1;
        this.header.tweenIn();
        if (this.bgTab) {
            TweenMax.to(this.bgTab.scale, 0.2, {
                x: 1,
                y: 1,
                ease: Back.easeOut,
            });
        }
        await waitForMs(100);
        if (this) {
            for (const element of this.tabs) {
                if (element && this.bgTab) {
                    element.alpha = 1;
                    TweenMax.to(element, 0.2, {
                        y: element.y - element.bgActive.height * 0.3,
                    });
                }

                await waitForMs(100);
            }
        }
    }

    public async tweenOut() {
        this.header.tweenOut();
        TweenMax.to(this, 0.2, {
            alpha: 0,
        });
        await waitForMs(200);

        this.allPages.forEach(tab => {
            tab.cleanUp();
        });
        this.allPages = [];

        this.btns.forEach(btn => {
            btn.cleanUp();
        });
        this.btns = [];

        this.tabs = [];

        this.removeAllListeners();

        TweenMax.killTweensOf(this.clouds);
        TweenMax.killTweensOf(this, true);
        TweenMax.killChildTweensOf(this, true);

        this.cleanup();
        this.destroy({ children: true }); // all the children will have their destroy method called as well
    }

    public swapActiveTab(index: number) {
        if (index !== this.curActiveTab) {
            TweenMax.to(this.allPagesContainer, 0.4, {
                x: -this.bgTab.width * index,
            });
        }
        this.curActiveTab = index;
    }

    /**
     * shows the player the reward if the reward counter reaches 10
     * called by hud
     */
    public async giveReward(reward: SocialRewardBox | RewardBox) {
        this.bg = new ScreenBlur();
        this.bg.interactive = true;

        SoundManager.playSound(GameAudio.WIN_JINGLE);

        this.confetti = new Confetti();

        const button = new TextButton({
            width: gameConfig.game_width * 0.4,
            height: gameConfig.game_height * 0.3,
            btnText: Core.local.getText('playon'),
        });
        button.x = getGameSize().gameWidth / 2;
        button.y = getGameSize().gameHeight * 0.8;
        button.alpha = 0;
        button.interactive = false;
        button.buttonMode = false;

        reward.x = getGameSize().gameWidth / 2 + gameConfig.game_width * 0.3;
        reward.y = getGameSize().gameHeight * 0.2;
        reward.alpha = 0;
        reward.scale.set(0.9);
        this.addChild(this.bg, button, reward, this.confetti);

        button.on('trigger', () => {
            this.tweenOutReward(button, reward).catch(err => {
                console.log([' error with tweenout reward', err]);
            });
        });

        /* reward.on(HudActions.OPEN_REWARD_BOX, (rewards: RewardGroup[]) => {
            this.emit(GameContainerAction.RECEIVE_REWARDS, rewards);
        }); */

        await waitForMs(500);
        TweenMax.to(reward, 1, {
            alpha: 1,
            x: getGameSize().gameWidth / 2,
            y: getGameSize().gameHeight * 0.3,
        });

        TweenMax.to(reward.scale, 1, {
            x: 1.5,
            y: 1.5,
        });
        await waitForMs(1000);
        await reward.openBox();
        TweenMax.to(button, 0.4, {
            alpha: 1,
        });
        button.interactive = true;
        button.buttonMode = true;
    }

    public update(...args: any[]) {}

    public resize(...args: any[]) {}

    public cleanup(...args: any[]) {}

    protected handleBtn(e: PIXI.interaction.InteractionEvent) {}

    /**
     * tweens the reward overlay out, then destroys its elements
     * @param button the button pressed to play on
     * @param reward the rewardbox object
     * @param popupAction action to trigger when reward fades out
     * @param actionArgs arguments passed to the action
     */
    protected async tweenOutReward(button: UIButton, reward: PIXI.Container) {
        button.interactive = false;
        button.buttonMode = false;

        TweenMax.to([reward, button], 0.2, {
            alpha: 0,
        });
        await waitForMs(400);
        if (this.confetti) {
            this.confetti.cleanUp();
        }
        reward.destroy();
        button.destroy();
        this.bg.destroy();
    }

    /**
     * sets the active tab
     * checks the sender of the event and sets it as the active tab
     */
    // todo add new tabs
    private handleTab(e: PIXI.interaction.InteractionEvent) {
        this.tabs.forEach(element => {
            if (this.tabs.indexOf(element) === this.tabs.indexOf(e.currentTarget as SocialTabBtn)) {
                element.setActiveTab(true);
                SagaTracking.trackTabClick(element.tabKey);
                this.swapActiveTab(this.tabs.indexOf(element));
            } else {
                element.setActiveTab(false);
            }
        });
    }

    /**
     * add clouds to the background
     * if its not a low end device, add animation to them
     */
    private addClouds() {
        for (let i = 0; i < 5; i++) {
            const cloud = new Sprite(PIXI.utils.TextureCache['commonui/leaderboard-bgcloud']);
            cloud.anchor.set(0.5);
            cloud.x = getGameSize().gameWidth * getRandomFloat(0.1, 0.9);
            cloud.y = getGameSize().gameHeight * getRandomFloat(0.1, 0.9);
            this.clouds.push(cloud);
        }

        this.animateClouds();
    }

    /**
     * animates the clouds so they slowly move to the left
     * if they are out of the screen they teleport back offscreen to the right
     */
    private animateClouds() {
        const animationDuration = 50;
        const gameWidth = getGameSize().gameWidth;
        const cloudWidth = this.clouds[0].width;
        const targetX = -cloudWidth;
        this.clouds.forEach(cloud => {
            TweenMax.to(cloud, (animationDuration * (cloud.x - targetX)) / gameWidth, {
                x: targetX,
                onComplete: () => {
                    TweenMax.fromTo(
                        cloud,
                        animationDuration,
                        { x: gameWidth + cloudWidth },
                        { x: targetX, repeat: -1, ease: Power0.easeNone }
                    );
                },
                ease: Power0.easeNone,
            });
        });
    }
}
